import React from 'react';
import InfoIcon from '../InfoIcon';
import OakStatusBadge from './OakStatusBadge';

// Quick ⓘ clarifications keyed by the card's label – short, non-duplicative
// of the help page (per the compliance info-icons brief).
const LIBRARY_INFO = {
  'Supplier Enhancement': { heading: 'Supplier enhancement', body: 'Optional documents that can unlock Fusion supplier programmes. These are separate from your required compliance – a bonus, not a requirement.' },
  'Terms & Conditions':   { heading: 'Terms & conditions',   body: 'The contract you signed when joining Fusion 2K. View it any time.' },
};

// Reference-material card on /portal/compliance – used twice on the
// page (T&Cs and Supplier Enhancement). Each card has a green eyebrow
// label, a short description, and a list of document rows underneath.
//
// Documents come from fusion_documents where document_type is
// 'terms_conditions' or 'supplier_enhancement' and visible_in_portal
// is true. The caller passes them in pre-fetched.

const FileTextIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#009159" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

// What the row's button should say for each kind of action. "Complete" rather
// than "Upload" on a form or a PDF, because the supplier isn't sending us a
// file in those cases — they fill it in here.
const ACTION_LABEL = {
  upload: 'Upload',
  form: 'Complete',
  pdf_sign: 'Read and sign',
  acknowledge: 'Read and confirm',
};

export default function LibraryCard({
  label, description, documents = [], ecoAward = false,
  // (doc) => { kind: 'upload'|'form'|'pdf_sign'|'acknowledge'|'done'|'none' }
  actionFor,
  onAction,
}) {
  return (
    <div className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift px-5 py-5">
      {/* Category heading – a real heading, sentence case, not the shouting
          eyebrow: caps wrap to two lines and read slowly
          (doc-library-placeholders brief §5, §8). */}
      <h3 className="font-['Metropolis'] text-[13px] font-bold text-fusion-forest mb-1.5 flex items-center gap-2">
        <span>{label}</span>
        {LIBRARY_INFO[label] && (
          <InfoIcon heading={LIBRARY_INFO[label].heading} ariaLabel={`What is ${LIBRARY_INFO[label].heading}?`}>{LIBRARY_INFO[label].body}</InfoIcon>
        )}
      </h3>
      {ecoAward && documents.length > 0 ? (
        // Eco-award category – say what completing these documents achieves and
        // show the growing-oak (acorn) illustration, instead of a generic blurb.
        <div className="flex items-center gap-3 mb-3 bg-[#92D400]/[0.06] border border-[#92D400]/25 rounded-xl px-3 py-2.5">
          <div className="flex-shrink-0">
            <OakStatusBadge approved={0} required={5} size={44} animate={false} />
          </div>
          <div className="font-['Metropolis'] text-[11px] text-[#51534A] leading-snug">
            Completing the documents here grows your <strong className="text-fusion-forest">Eco recognition</strong> – from <strong className="text-ink">Acorn</strong> towards full <strong className="text-fusion-forest">Oak Status</strong>.
          </div>
        </div>
      ) : (
        <div className="font-['Metropolis'] text-[11px] text-[#51534A] leading-snug mb-3">
          {description}
        </div>
      )}
      {documents.length === 0 ? (
        <div className="font-['Metropolis'] text-[11px] text-[#51534A] italic py-2">
          Nothing here yet.
        </div>
      ) : (
        documents.map(doc => {
          const action = actionFor ? actionFor(doc) : null;
          return (
            <div
              key={doc.id}
              className="flex items-center gap-3 py-2 border-t border-line first:border-t-0"
            >
              <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#eef2e3' }}>
                <FileTextIcon />
              </div>
              <div className="flex-1 min-w-0">
                {/* Title wraps rather than truncating mid-word. Suppliers don't
                    need the internal version number, so it isn't shown here. */}
                <div className="font-['Metropolis'] text-[12px] text-ink font-bold break-words">
                  {doc.document_name}
                </div>
              </div>
              {/* The ACTION comes first. A library document the supplier can
                  complete used to offer nothing but View — or, with no file to
                  view, a dead-end label. That is why the Environmental &
                  Sustainability Questionnaire sat published, visible and inert
                  while the card above it promised that completing these grows
                  their Eco recognition.
                  These are optional: nothing here affects compliance status or
                  blocks an award (Rob, 2026-08-14). */}
              {action && action.kind !== 'none' && action.kind !== 'done' ? (
                <button
                  type="button"
                  onClick={() => onAction?.(doc)}
                  className="font-['Metropolis'] text-[10px] font-bold rounded-full px-3 py-1.5 border border-[#2E8FA1]/40 text-[#2E8FA1] hover:border-[#2E8FA1] hover:bg-[#2E8FA1]/[0.06] transition-colors flex-shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2E8FA1]">
                  {ACTION_LABEL[action.kind] || 'Complete'}
                </button>
              ) : action?.kind === 'done' ? (
                <span className="font-['Metropolis'] text-[9px] font-bold uppercase tracking-[0.15em] text-fusion-forest flex-shrink-0 inline-flex items-center gap-1">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                  Sent
                </span>
              ) : doc.file_url ? (
                <a
                  href={doc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-['Metropolis'] text-[9px] font-extrabold uppercase tracking-[0.18em] text-fusion-forest hover:text-ink flex-shrink-0"
                >
                  View
                </a>
              ) : (
                // Two earlier labels here were both wrong, for opposite
                // reasons, and the honest one is neither.
                //
                //   "Coming Soon"          – shown for ANY document with no
                //     file. But an upload / form / pdf_sign document has no
                //     file by design, so a live document Fusion is waiting on
                //     read as "nothing needed from you".
                //   "In your requirements" – my replacement, and worse: it
                //     sent the supplier looking in their requirements list for
                //     something that is NOT there. Library documents come from
                //     a `shown` category; requirements come from `required`
                //     categories via the risk tiers. A document can only be in
                //     one of the two.
                //
                // So the truth is: this is optional material Fusion has
                // published, there is nothing to download, and there is
                // currently no way to complete it from here. Saying "ask us"
                // is the only thing that doesn't send someone in a circle.
                //
                // ⚠️ THE REAL FIX IS NOT A LABEL. Either the library needs to
                // open actionable documents (they feed the Eco recognition, so
                // suppliers are meant to complete them), or the category needs
                // to be able to say "required of everyone, not by risk tier".
                // Both are queued. Until one lands, this row is honest about
                // being a dead end rather than pretending otherwise.
                <span
                  title="Fusion 2K has published this but there is nothing to download and no way to complete it in the portal yet. Ask your Fusion contact if you need it."
                  className="font-['Metropolis'] text-[9px] font-bold uppercase tracking-[0.15em] text-[#51534A] flex-shrink-0">
                  Ask Fusion 2K
                </span>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
